import React, { useRef, useState, memo, useCallback, useEffect, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch } from 'react-redux';

import CommonLoader from '../../../components/common-loader/CommonLoader';
import ConfirmationModal from '../../../components/confirmation-modal/ConfirmationModal';
import MessageInfoModal from '../message-info-modal/MessageInfoModal';
import EmptyMessageCard from '../message-card/EmptyMessageCard';
import { ProjectContext } from '../../../context/ProjectContext';

import scss from './MessagesList.module.scss';

const MessagesList = ({
  content = [],
  isLoading,
  bindNextAction,
  isActionsDisabled,
  maxHeight,
  onLike,
  onSelect,
  onDelete,
  onRestore,
  onSaveMessage,
  getMessageLogs,
  setEditableMessage,
  MessageCard,
  logsSelector,
  onOpenImages,
}) => {
  const { t } = useTranslation('scout');
  const scrollableRef = useRef(null);
  const dispatch = useDispatch();
  const projectContext = useContext(ProjectContext);
  const actions = projectContext?.actions;

  const [selectedMessage, setSelectedMessage] = useState({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [isInfoModalOpen, setIsInfoModalOpen] = useState(false);

  const handleStartEdit = useCallback(
    ({ id, message }) => {
      bindNextAction(() => dispatch(setEditableMessage({ id, message })));
    },
    [dispatch, bindNextAction, setEditableMessage],
  );

  const handleCloseModals = useCallback(() => {
    setIsDeleteModalOpen(false);
    setIsInfoModalOpen(false);
  }, []);

  const handleOpenDeleteModal = useCallback(
    (message) => {
      bindNextAction(() => {
        setSelectedMessage(message);
        setIsDeleteModalOpen(true);
      });
    },
    [bindNextAction],
  );

  const handleOpenInfoModal = useCallback(
    (message) => {
      bindNextAction(() => {
        setSelectedMessage(message);
        setIsInfoModalOpen(true);
      });
    },
    [bindNextAction],
  );

  const handleSelectMessage = ({ id, selected, ...rest }) => {
    if (onSelect) {
      dispatch(onSelect({ id, selected, ...rest }));
      return;
    }
    actions?.('selectMessage', { id, selected, ...rest });
  };

  const handleDeleteMessage = () => {
    if (onDelete) {
      dispatch(onDelete({ id: selectedMessage.id }));
      return;
    }
    actions?.('deleteMessage', { id: selectedMessage.id });
  };

  const handleLikeMessage = ({ id, reaction, createType }) => {
    if (onLike) {
      dispatch(onLike({ id, reaction, createType }));
      return;
    }
    actions?.('likeMessage', { id, reaction, createType });
  };

  const handleRestoreMessage = (id) => {
    if (onRestore) {
      dispatch(onRestore({ id }));
      return;
    }
    actions?.('restoreMessage', { id });
  };

  const handleOpenImages = () => {
    onOpenImages?.(selectedMessage);
  };

  useEffect(() => {
    if (maxHeight) {
      scrollableRef.current.style.maxHeight = maxHeight;
    }
  }, [maxHeight]);

  return (
    <div className={scss.messagesContent}>
      <div className={scss.messagesList} ref={scrollableRef}>
        {content.map((message) => (
          <MessageCard
            key={message.id}
            message={message}
            onEditStart={handleStartEdit}
            isLoading={isLoading}
            onDelete={handleOpenDeleteModal}
            onInfo={handleOpenInfoModal}
            onLike={handleLikeMessage}
            onSelect={handleSelectMessage}
            onRestore={handleRestoreMessage}
            onSaveMessage={onSaveMessage}
            isActionsDisabled={isActionsDisabled}
          />
        ))}

        {content.length === 0 ? <EmptyMessageCard /> : null}
      </div>

      <CommonLoader isLoading={isLoading} scrollableRef={scrollableRef} disableScrolling />

      <ConfirmationModal
        isOpen={isDeleteModalOpen}
        onClose={handleCloseModals}
        onCancel={handleCloseModals}
        onConfirm={handleDeleteMessage}
        subLabel={t('doYouReallyWantToDelete')}
      />

      <MessageInfoModal
        isOpen={isInfoModalOpen}
        onClose={handleCloseModals}
        message={selectedMessage}
        getMessageLogs={getMessageLogs}
        selector={logsSelector}
        onOpenImages={handleOpenImages}
      />
    </div>
  );
};

export default memo(MessagesList);
